import './Graphic.scss';
import * as THREE from 'three';
import React, { useEffect, useRef } from 'react';
import { Grid } from '@mui/material';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import * as TWEEN from '@tweenjs/tween.js';
import { useLocation } from 'react-router-dom';
import { useAppDispatch } from '../../../../reduxService/hooks';
import { IGraphicProps } from './IGraphic';

/* NON SI USA; TENUTO PER ESEMPIO 3D */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
export const Graphic = (props: IGraphicProps) => {
    const mountRef = useRef<HTMLDivElement>(null);
    const sceneRef = useRef<THREE.Scene>();
    const cameraRef = useRef<THREE.PerspectiveCamera>();
    const rendererRef = useRef<THREE.WebGLRenderer>();
    const [img, setImg] = React.useState('');
    const location = useLocation();

    const inResults = location.pathname.split('/')[1] === 'results';

    const dispatch = useAppDispatch();

    useEffect(() => {
        // Initialize Three.js components when the component mounts
        if (!mountRef.current) return;

        const slopeInclination = 0;
        const horizontalSpacing = 0;
        const verticaSpacing = 0;

        // Create a scene
        const scene = new THREE.Scene();
        sceneRef.current = scene;

        const width = mountRef.current.offsetWidth * 0.9 > 700 ? mountRef.current.offsetWidth * 0.9 : 700;
        const height = mountRef.current.offsetHeight * 0.9 > 700 ? mountRef.current.offsetHeight * 0.9 : 700;

        // Create a camera
        const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
        camera.position.set(2.2, 1.5, 3.1);
        cameraRef.current = camera;

        // Create a renderer
        const renderer = new THREE.WebGLRenderer({ alpha: true });
        // Set the size of the renderer to the size of its container
        renderer.setSize(width, height);
        rendererRef.current = renderer;

        // Append the renderer to the mount point
        mountRef.current.appendChild(renderer.domElement);

        const controls = new OrbitControls(camera, renderer.domElement);

        controls.enableRotate = false;
        controls.enableZoom = false;
        controls.minDistance = 3;
        controls.maxDistance = 5;

        // Define event handlers for proper cleanup
        const zoomInHandler = (event: Event) => {
            tween('in');
        };

        const zoomOutHandler = (event: Event) => {
            tween('out');
        };

        const zoomResetHandler = (event: Event) => {
            tween('reset');
        };

        // Get button elements
        const btnZoomIn = document.getElementById('btnZoomIn');
        const btnZoomOut = document.getElementById('btnZoomOut');
        const btnZoomReset = document.getElementById('btnZoomReset');

        // Add event listeners
        btnZoomIn?.addEventListener('click', zoomInHandler);
        btnZoomOut?.addEventListener('click', zoomOutHandler);
        btnZoomReset?.addEventListener('click', zoomResetHandler);

        function tween(type: string) {
            // in - true, out - false

            const dir = new THREE.Vector3();
            camera.getWorldDirection(dir);
            dir.negate();
            const dist = controls.getDistance();

            let desiredDistance = 0;

            if (type === 'in') {
                desiredDistance = controls.minDistance;
            } else if (type === 'out') {
                desiredDistance = controls.maxDistance;
            } else if (type === 'reset') {
                desiredDistance = controls.minDistance + 1;
            }

            new TWEEN.Tween({ val: dist })
                .to({ val: desiredDistance }, 1000)
                .onUpdate(val => {
                    camera.position.copy(controls.target).addScaledVector(dir, val.val);
                })
                .start();
        }

        renderer.setClearColor(0x000000, 0);

        // Load the texture image
        const textureLoader = new THREE.TextureLoader();

        createRockSlopeShape(slopeInclination, scene, renderer, textureLoader);

        createRockSlopeBorder(scene, slopeInclination);

        createPlane(1, 1.5, calculateTouchPointX(slopeInclination) + 0.5, 0, 0.75, Math.PI / 2, scene, renderer, textureLoader);

        createPlaneAnchors(scene, renderer, textureLoader, horizontalSpacing, verticaSpacing, slopeInclination);

        // Animation loop
        const animate = () => {
            requestAnimationFrame(animate);
            TWEEN.update();
            renderer.render(scene, camera);
            saveCanvasAsImage(renderer.domElement);
        };

        if (horizontalSpacing != 0 && verticaSpacing != 0 && slopeInclination != 0) {
            animate();
        }

        // Cleanup function
        return () => {
            // Critical: Remove zoom button event listeners to prevent memory leaks
            btnZoomIn?.removeEventListener('click', zoomInHandler);
            btnZoomOut?.removeEventListener('click', zoomOutHandler);
            btnZoomReset?.removeEventListener('click', zoomResetHandler);

            mountRef.current?.removeChild(renderer.domElement);
        };
    }, []);

    const saveCanvasAsImage = (domElement: HTMLCanvasElement) => {
        const canvas = domElement;
        if (canvas) {
            const src = canvas.toDataURL();
            // Save the image
            localStorage.setItem('graphic', src);
        }
    };

    const calculateTouchPointX = (betaDegrees: number): number => {
        // Convert beta from degrees to radians
        const betaRadians = THREE.MathUtils.degToRad(betaDegrees);

        // Calculate x coordinate using the equation x = 1 / tan(beta)
        const xCoordinate = 1 / Math.tan(betaRadians);

        return xCoordinate;
    };

    const calculateX = (y: number, angleDegrees: number) => {
        // Convert angle from degrees to radians
        const angleRadians = angleDegrees * (Math.PI / 180);

        // Calculate x using the formula x = y / tan(angle)
        const x = (1.1 - y) / Math.tan(angleRadians);

        return x;
    };

    const calculateScaledValue = (value: number, slopeTotalHeight: number): number => {
        const highestPoint = 1;
        const scalingFactor = highestPoint / slopeTotalHeight;
        return value * scalingFactor;
    };

    const getRockSlopeShape = (slopeInclination: number) => {
        const shape = new THREE.Shape();
        shape.lineTo(0, 1);
        shape.lineTo(calculateTouchPointX(slopeInclination), 0);
        shape.lineTo(0, 0);

        return shape;
    };

    const createRockSlopeShape = (slopeInclination: number, scene: THREE.Scene, renderer: THREE.WebGLRenderer, textureLoader: THREE.TextureLoader) => {
        const shape = getRockSlopeShape(slopeInclination);

        // Define the extrusion settings
        const extrudeSettings = {
            steps: 1,
            depth: 1.5, // The depth of the extrusion
            bevelEnabled: false,
        };

        // Create the shape
        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

        // Create material with texture

        // Add the mesh to your scene
    };

    const createRockSlopeBorder = (scene: THREE.Scene, slopeInclination: number) => {
        //Create the line
        const points = [];
        points.push(new THREE.Vector3(0, 1, 0));
        points.push(new THREE.Vector3(calculateTouchPointX(slopeInclination), 0, 0));
        points.push(new THREE.Vector3(calculateTouchPointX(slopeInclination), 0, 1.5));
        points.push(new THREE.Vector3(0, 1, 1.5));

        const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
        const lineMaterial = new THREE.LineBasicMaterial({ color: 0x8b4513 }); // Brown color
        const line = new THREE.Line(lineGeometry, lineMaterial);

        scene.add(line);
    };

    const createPlane = (width: number, height: number, x: number, y: number, z: number, rotationX: number, scene: THREE.Scene, renderer: THREE.WebGLRenderer, textureLoader: THREE.TextureLoader) => {
        // Create a material
        const planeGeometry = new THREE.PlaneGeometry(width, height);
    };

    const selectNetTexture = (meshTypeName?: string) => {
        //select the substring till the first space
    };

    const createNet = (scene: THREE.Scene, textureLoader: THREE.TextureLoader, renderer: THREE.WebGLRenderer, slopeInclination: number, netImage: string) => {
        const point1 = new THREE.Vector3(0, 0, 0);
        const point3 = new THREE.Vector3(0, 1, 0);
        const point2 = new THREE.Vector3(calculateTouchPointX(slopeInclination), 0, 0);

        const rockSlopeShape = getRockSlopeShape(slopeInclination);

        const points = [];
        points.push(new THREE.Vector3(0, 1, 0));
        points.push(new THREE.Vector3(0, 1, 1));
        points.push(new THREE.Vector3(calculateTouchPointX(slopeInclination), 0, 0));
        points.push(new THREE.Vector3(calculateTouchPointX(slopeInclination), 0, 1));

        const shape = new THREE.Shape();
        shape.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) {
            shape.lineTo(points[i].x, points[i].y);
        }
        shape.lineTo(points[0].x, points[0].y); // Close the shape

        // Define extrusion settings
        const extrudeSettings = {
            depth: 1.5, // Depth of the extrusion
            bevelEnabled: false, // Disable bevel for simplicity
            bevelSegments: 10,
        };

        // Create the geometry by extruding the shape
        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

        const netTexture = textureLoader.load(netImage, () => {
            netTexture.wrapS = THREE.RepeatWrapping;
            netTexture.wrapT = THREE.RepeatWrapping;
            netTexture.repeat.set(8, 8); // Set the desired number of repetitions
            netTexture.anisotropy = renderer.capabilities.getMaxAnisotropy();
            netTexture.colorSpace = THREE.SRGBColorSpace;
        });

        // Create a material
        const material = new THREE.MeshBasicMaterial({ map: netTexture, side: THREE.DoubleSide, transparent: true }); // Green color

        // Create a mesh using the geometry and material
        const mesh = new THREE.Mesh(geometry, material);
        mesh.renderOrder = 1;

        // Add the mesh to the scene
        scene.add(mesh);
    };

    const createAnchors = (scene: THREE.Scene, renderer: THREE.WebGLRenderer, textureLoader: THREE.TextureLoader, x: number, y: number, z: number, scaledHorizontalSpacing: number) => {
        // Create small sphere geometry for anchor points
        const anchorGeometry = new THREE.SphereGeometry(0.02, 8, 8);

        // Create material for anchor points (red color for visibility)
        const anchorMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });

        // Create mesh combining geometry and material
        const anchorMesh = new THREE.Mesh(anchorGeometry, anchorMaterial);

        // Position the anchor at the specified coordinates
        anchorMesh.position.set(x, y, z);

        // Add the anchor to the scene
        scene.add(anchorMesh);
    };

    function createPlaneAnchors(scene: THREE.Scene, renderer: THREE.WebGLRenderer, textureLoader: THREE.TextureLoader, horizontalSpacing: number, verticaSpacing: number, slopeInclination: number) {
        const scaledHorizontalSpacing = calculateScaledValue(horizontalSpacing, 12);
        const scaledVerticaSpacing = calculateScaledValue(verticaSpacing, 12);

        //console.log(scaledHorizontalSpacing, scaledVerticaSpacing);

        let y = 0.95;
        let z = 1.45;
        let x = calculateX(y, slopeInclination);

        for (let i = 0; i < 1000; i++) {
            if (i == 0) {
                createAnchors(scene, renderer, textureLoader, x, y, z, scaledHorizontalSpacing);
                continue;
            }

            if (z - scaledHorizontalSpacing <= 0.1) {
                z = 1.45; // Reset z to the start of the row

                if (y - scaledVerticaSpacing >= 0.1) {
                    y -= scaledVerticaSpacing; // Move to the next row
                    x = calculateX(y, slopeInclination); // Reset x to the start of the row
                    createAnchors(scene, renderer, textureLoader, x, y, z, scaledHorizontalSpacing);
                } else {
                    break;
                }
            } else {
                z -= scaledHorizontalSpacing; // Update z if necessary
                createAnchors(scene, renderer, textureLoader, x, y, z, scaledHorizontalSpacing);
            }
        }
    }

    return (
        <Grid container id="container" spacing={2}>
            <Grid ref={mountRef} id="graphic" item md={11} xs={11} className="canvas-container"></Grid>
            {!inResults && (
                <Grid item md={1} xs={1} id="zoom">
                    Graph dynamicProject
                    <button type="button" id="btnZoomIn">
                        <i className="fa-solid fa-plus" />
                    </button>
                    <button id="btnZoomOut">
                        <i className="fa-solid fa-minus" />
                    </button>
                    <button id="btnZoomReset">
                        <i className="fa-solid fa-expand" />
                    </button>
                </Grid>
            )}
        </Grid>
    );
};

export default Graphic;
